import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { local, errMsg } from "@/lib/api";

const COMPANION = process.env.REACT_APP_BACKEND_URL;

// Per-organisation branded login at fhapas.com/<slug>/<locality>
export default function BrandLogin() {
  const { slug, locality } = useParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    local.get(`/brand/${slug}/${locality}`)
      .then((r) => setBrand(r.data))
      .catch(() => setNotFound(true));
  }, [slug, locality]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email.trim(), password);
      localStorage.setItem("fhapas_brand_path", `${slug}/${locality}`);
      toast.success(`Welcome back, ${user.full_name}`);
      navigate(user.role === "superadmin" ? "/superadmin" : "/app/dashboard");
    } catch (err) {
      toast.error(errMsg(err, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  if (notFound) {
    return (
      <AuthShell title="Organisation not found" subtitle="This branded link isn't recognised."
        footer={<Link to="/login" className="font-semibold text-teal-700 hover:underline">Go to standard sign in</Link>}>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          The link <b>/{slug}/{locality}</b> doesn't match any organisation. Please check with your administrator.
        </div>
      </AuthShell>
    );
  }

  const color = brand?.brand_color || "#0F766E";
  const logoUrl = brand?.org_id ? `${COMPANION}/api/public/logo/${brand.org_id}` : "";

  return (
    <AuthShell title={brand?.name || "Sign in"} subtitle="Your organisation workspace"
      footer={<>Not your organisation? <Link to="/login" className="font-semibold text-teal-700 hover:underline">Standard sign in</Link></>}>
      <div className="mb-6 flex items-center gap-3">
        {logoUrl && <img src={logoUrl} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-14 w-14 rounded-xl border border-slate-200 bg-white object-contain p-1" />}
        <div>
          <p className="font-display text-lg font-bold" style={{ color }}>{brand?.name || "…"}</p>
          <p className="text-xs text-slate-500">Powered by FHAPAS</p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label>Email</Label>
          <Input data-testid="brand-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
        <div className="space-y-1.5"><Label>Password</Label>
          <PasswordInput data-testid="brand-login-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
        <Button data-testid="brand-login-submit" type="submit" disabled={loading} className="w-full text-white hover:opacity-90" style={{ backgroundColor: color }}>
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}
